import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { PowerBIEmbed } from "powerbi-client-react";
import { models, Report } from "powerbi-client";
import { MdSyncProblem, MdTvOff } from "react-icons/md";
import { getTvEmbed, getTvPlaylist, TvError } from "../../services/tvApi";
import { EmbedViewModel } from "../../interfaces/Paineis";
import { tipoDeTokenPowerBi } from "../admin/paineis/tipoToken";
import { TvPlaylistItem, TvPlaylistResposta } from "../../interfaces/PaineisTvTema";
import {
  esperaReconexao,
  itensMudaram,
  momentoDeRenovar,
  PLAYLIST_RECHECK_MS,
  proximoIndice,
  variaveisDoTema,
} from "./agenda";

const ROTULO_FRESCOR: Record<TvPlaylistItem["frescor"], string> = {
  EmDia: "Em dia",
  Atrasado: "Atrasado",
  Falhou: "Falha na atualização",
  SemSla: "",
};

const COR_FRESCOR: Record<TvPlaylistItem["frescor"], string> = {
  EmDia: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  Atrasado: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  Falhou: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  SemSla: "",
};

/** Qualquer erro que não seja resposta HTTP da API (fetch rejeitado = sem rede). */
const ehRede = (e: unknown) => !(e instanceof TvError);

/* ---------------- Um relatório (atual ou pré-carregado) ---------------- */

interface SlideProps {
  chave: string;
  item: TvPlaylistItem;
  visivel: boolean;
  onReconectando: (indice: number, reconectando: boolean) => void;
  onDesativada: () => void;
}

const Slide: React.FC<SlideProps> = ({ chave, item, visivel, onReconectando, onDesativada }) => {
  const [config, setConfig] = useState<models.IReportEmbedConfiguration | null>(null);
  const [indisponivel, setIndisponivel] = useState(false);
  const reportRef = useRef<Report | null>(null);
  const callbacks = useRef({ onReconectando, onDesativada });
  callbacks.current = { onReconectando, onDesativada };

  useEffect(() => {
    let vivo = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let falhas = 0;
    let primeira = true;

    const carregar = async () => {
      try {
        const e: EmbedViewModel = await getTvEmbed(chave, item.indice);
        if (!vivo) return;
        falhas = 0;
        callbacks.current.onReconectando(item.indice, false);
        setIndisponivel(false);
        if (primeira) {
          primeira = false;
          // Configuração fixa: o token novo entra só por setAccessToken, sem recriar o iframe.
          setConfig({
            type: "report",
            id: e.reportId,
            embedUrl: e.embedUrl,
            accessToken: e.accessToken,
            tokenType: tipoDeTokenPowerBi(e.tipoToken),
            pageName: item.aba ?? undefined,
            settings: { navContentPaneEnabled: false, filterPaneEnabled: false },
          });
        } else {
          await reportRef.current?.setAccessToken(e.accessToken);
        }
        timer = setTimeout(carregar, Math.max(1000, momentoDeRenovar(e.expiresOn)));
      } catch (err) {
        if (!vivo) return;
        if (err instanceof TvError && err.status === 401) {
          callbacks.current.onDesativada();
          return;
        }
        if (ehRede(err)) {
          callbacks.current.onReconectando(item.indice, true);
          timer = setTimeout(carregar, esperaReconexao(falhas++));
        } else {
          // 404/503: relatório fora do ar; a TV segue o rodízio e tenta de novo em 1 min.
          setIndisponivel(true);
          primeira = true;
          setConfig(null);
          timer = setTimeout(carregar, 60_000);
        }
      }
    };

    void carregar();
    return () => {
      vivo = false;
      if (timer) clearTimeout(timer);
      callbacks.current.onReconectando(item.indice, false);
    };
  }, [chave, item.indice, item.aba]);

  return (
    <div className={`absolute inset-0 ${visivel ? "" : "invisible"}`} aria-hidden={!visivel}>
      {config ? (
        <PowerBIEmbed
          embedConfig={config}
          getEmbeddedComponent={(r) => {
            reportRef.current = r as Report;
          }}
          cssClassName="h-full w-full [&>iframe]:h-full [&>iframe]:w-full [&>iframe]:border-0"
        />
      ) : indisponivel ? (
        <p role="alert" className="flex h-full items-center justify-center text-xl text-[var(--dv-text-muted)]">
          Este painel está indisponível no momento. A TV tentará de novo em instantes.
        </p>
      ) : (
        <div className="h-full w-full animate-pulse bg-[var(--dv-surface)]" aria-busy="true" aria-label="Carregando painel" />
      )}
    </div>
  );
};

/* ---------------- Página ---------------- */

const TvPage: React.FC = () => {
  const { codigo = "" } = useParams();
  const [playlist, setPlaylist] = useState<TvPlaylistResposta | null>(null);
  const [desativada, setDesativada] = useState(false);
  const [erroInicial, setErroInicial] = useState(false);
  const [redePlaylist, setRedePlaylist] = useState(false);
  const [slidesOffline, setSlidesOffline] = useState<number[]>([]);
  const [atual, setAtual] = useState(0);
  const [geracao, setGeracao] = useState(0);
  const [agora, setAgora] = useState(() => new Date());
  const playlistRef = useRef<TvPlaylistResposta | null>(null);

  const itens = useMemo(() => playlist?.itens ?? [], [playlist]);

  const aoReconectar = useCallback((indice: number, reconectando: boolean) => {
    setSlidesOffline((s) =>
      reconectando ? (s.includes(indice) ? s : [...s, indice]) : s.includes(indice) ? s.filter((i) => i !== indice) : s
    );
  }, []);
  const aoDesativar = useCallback(() => setDesativada(true), []);

  // Playlist: busca inicial e a cada 5 min; 401 encerra tudo, falha tenta de novo com espera crescente.
  useEffect(() => {
    if (!codigo) {
      setErroInicial(true);
      return;
    }
    let vivo = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let falhas = 0;

    const buscar = async () => {
      try {
        const nova = await getTvPlaylist(codigo);
        if (!vivo) return;
        falhas = 0;
        setRedePlaylist(false);
        setErroInicial(false);
        const anterior = playlistRef.current;
        playlistRef.current = nova;
        setPlaylist(nova);
        if (!anterior || itensMudaram(anterior.itens, nova.itens)) {
          setAtual(0);
          setGeracao((g) => g + 1);
        }
        timer = setTimeout(buscar, PLAYLIST_RECHECK_MS);
      } catch (e) {
        if (!vivo) return;
        if (e instanceof TvError && e.status === 401) {
          setDesativada(true);
          return;
        }
        if (ehRede(e)) setRedePlaylist(true);
        else setErroInicial(!playlistRef.current);
        timer = setTimeout(buscar, esperaReconexao(falhas++));
      }
    };
    void buscar();
    return () => {
      vivo = false;
      if (timer) clearTimeout(timer);
    };
  }, [codigo]);

  // Rodízio: troca após `segundos` do item atual.
  const atualItem = itens[atual];
  useEffect(() => {
    if (desativada || itens.length < 2 || !atualItem) return;
    const t = setTimeout(
      () => setAtual((i) => proximoIndice(i, itens.length)),
      Math.max(5, atualItem.segundos) * 1000
    );
    return () => clearTimeout(t);
  }, [atual, geracao, atualItem, itens.length, desativada]);

  // Relógio HH:mm: atualiza uma vez por minuto.
  useEffect(() => {
    const t = setInterval(() => setAgora(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const estilo = useMemo(() => variaveisDoTema(playlist?.tema?.tokens) as React.CSSProperties, [playlist]);
  const reconectando = redePlaylist || slidesOffline.length > 0;
  const proximo = itens.length > 1 ? proximoIndice(atual, itens.length) : -1;
  const hora = agora.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  const moldura =
    "flex h-screen w-screen flex-col bg-[var(--dv-canvas)] text-[var(--dv-text)] dark:bg-[var(--dv-canvas)] dark:text-[var(--dv-text)]";

  if (desativada) {
    return (
      <div className={`${moldura} items-center justify-center gap-4`} style={estilo} role="alert">
        <MdTvOff className="h-16 w-16 text-[var(--dv-text-muted)]" aria-hidden="true" />
        <h1 className="text-3xl font-extrabold">TV desativada</h1>
        <p className="text-lg text-[var(--dv-text-muted)]">Peça ao administrador um novo endereço para esta TV.</p>
      </div>
    );
  }

  if (!playlist) {
    return (
      <div className={`${moldura} items-center justify-center gap-3`} style={estilo} aria-busy={!erroInicial && !redePlaylist}>
        {erroInicial || redePlaylist ? (
          <p role="alert" className="flex items-center gap-2 text-lg text-[var(--dv-text-muted)]">
            <MdSyncProblem className="h-6 w-6" aria-hidden="true" />
            {redePlaylist
              ? "Reconectando…"
              : "Não foi possível abrir a TV. Confira o endereço; a TV tentará de novo sozinha."}
          </p>
        ) : (
          <div
            className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--dv-border)] border-t-brand-500 dark:border-t-brand-500"
            role="status"
            aria-label="Carregando TV"
          />
        )}
      </div>
    );
  }

  return (
    <div className={moldura} style={estilo}>
      <main className="relative min-h-0 flex-1">
        {itens.length === 0 ? (
          <p className="flex h-full items-center justify-center text-xl text-[var(--dv-text-muted)]">
            Esta playlist ainda não tem painéis. Peça ao administrador para adicioná-los.
          </p>
        ) : (
          itens.map((it, i) =>
            i === atual || i === proximo ? (
              <Slide
                key={`${geracao}-${it.indice}`}
                chave={codigo}
                item={it}
                visivel={i === atual}
                onReconectando={aoReconectar}
                onDesativada={aoDesativar}
              />
            ) : null
          )
        )}
      </main>

      <footer className="flex items-center justify-between gap-4 border-t border-[var(--dv-border)] bg-[var(--dv-surface)] px-6 py-2 text-[var(--dv-text)] dark:bg-[var(--dv-surface)]">
        <div className="flex min-w-0 items-center gap-3">
          <span className="truncate text-base font-bold">{atualItem?.nome ?? playlist.nome}</span>
          {atualItem && ROTULO_FRESCOR[atualItem.frescor] && (
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${COR_FRESCOR[atualItem.frescor]}`}>
              {ROTULO_FRESCOR[atualItem.frescor]}
            </span>
          )}
          {reconectando && (
            <span
              role="status"
              className="flex items-center gap-1 text-sm font-semibold text-amber-700 dark:text-amber-300"
            >
              <MdSyncProblem className="h-4 w-4" aria-hidden="true" />
              Reconectando…
            </span>
          )}
        </div>
        <time dateTime={agora.toISOString()} className="text-lg font-extrabold tabular-nums">
          {hora}
        </time>
      </footer>
    </div>
  );
};

export default TvPage;
